import { Group, Quaternion, Vector3 } from "three";
import { Module } from "../module";
import { camera } from "../rendering/camera";
import { animationFrame$ } from "../../messages/xr/AnimationFrame";
import { renderManager } from "../xr/RenderManager";
import { localPlayerContainer } from "../localplayer/LocalPlayerContainer";
import { peerService } from "../multiplayer/peer";
import { serverConnection } from "../multiplayer/server/ServerConnectionService";
import { now } from "../../messages/timing";
import { PeerStateChangedMessage } from "../../messages/session/PeerMessages";
import { usersService } from "../multiplayer/UsersService";
import { MessageService } from "../../ui/services/message.service";
import { Text } from "troika-three-text";
const MAX_MESSAGE_WIDTH = 20;
const MESSAGE_RELATIVE_POSITON_VR = new Vector3(0, -0.15, -0.5);
const MESSAGE_RELATIVE_POSITION_DESKTOP = new Vector3(0, 0.18, -0.5);
const TEXT_SCALE = new Vector3(0.05, 0.05, 0.05);
const TEXT_ORIENTATION = new Quaternion(0, 1, 0, 0);
/**
 * Tracks the observable network health of connected peers so the client can surface connectivity changes and react to them consistently.
 *
 * Messages:
 * - `PeerStateChangedMessage` - Reacts to peer connection, ICE and signaling state changes by showing an on-screen (VR) or toast (desktop) error message, clearing it again once the corresponding state recovers.
 */
export class NetworkStatusModule extends Module {
    /**
     * Creates the status message group, parents it under the local player and subscribes to peer connection state
     * changes.
     */
    constructor() {
        super("NetworkStatusModule");
        this.group = new Group();
        this.statusMessages = [];
        this.needsTransformUpdate = false;
        this.needsGroupUpdate = false;
        this.lastCheckTime = 0;
        this.peerConnectionError = false;
        this.peerSignalingError = false;
        this.peerIceConnectionError = false;
        this.peersWithICEError = new Set();
        this.update = () => {
            if (!this.textMesh) {
                return;
            }
            const time = now();
            if (time - this.lastCheckTime > 5000) {
                this.lastCheckTime = time;
                this.checkOnlineStatus();
            }
            if (this.needsTransformUpdate) {
                this.needsTransformUpdate = false;
                this.needsGroupUpdate = false;
                let offset = 0;
                for (const data of this.statusMessages) {
                    if (data.textMesh) {
                        data.textMesh.position.copy(new Vector3(0, offset, 0));
                        offset -= 0.02;
                        this.needsGroupUpdate = true;
                    }
                }
            }
            if (!this.needsGroupUpdate) {
                return;
            }
            const pos = this.group.position.copy(renderManager.isXRPresenting() ? MESSAGE_RELATIVE_POSITON_VR : MESSAGE_RELATIVE_POSITION_DESKTOP);
            pos.applyQuaternion(camera.perspectiveCam.quaternion);
            pos.add(camera.perspectiveCam.position);
            this.group.quaternion.copy(camera.perspectiveCam.quaternion);
            this.group.updateMatrix();
            this.textMesh.sync();
        };
        this.handlePeerStateChangedMessage = (msg) => {
            switch (msg.eventType) {
                case "connection":
                    if (msg.state === "failed" || msg.state === "disconnected" || msg.state === "closed") {
                        this.peerConnectionError = true;
                        this.addMessage(1, "Error on a peer connection");
                    }
                    break;
                case "ice":
                    if (msg.state === "closed" || msg.state === "disconnected" || msg.state === "failed") {
                        this.peersWithICEError.add(msg.clientId);
                        const names = this.getNamesFromPeersWithICEEerrors();
                        this.peerIceConnectionError = true;
                        this.addMessage(3, `Error on ICE connection (${names})`);
                    }
                    break;
                case "signaling":
                    if (msg.state === "closed") {
                        this.peerSignalingError = true;
                        this.addMessage(2, "Lost connection to signaling server");
                    }
                    break;
                default:
                    console.warn(`Got unhandled eventType ${msg.eventType} with state ${msg.state}`);
                    break;
            }
        };
        this.group.frustumCulled = false;
        this.group.matrixAutoUpdate = false;
        localPlayerContainer.localPlayerRootNode.add(this.group);
        this.connection.subscribeByType(PeerStateChangedMessage, this.handlePeerStateChangedMessage);
    }
    /**
     * Prepares the text mesh and the four status message slots (server connection, peer connection, signaling and
     * ICE errors), then registers the per-frame update loop.
     */
    init() {
        this.textMesh = new Text();
        this.statusMessages.push({});
        this.statusMessages.push({});
        this.statusMessages.push({});
        this.statusMessages.push({});
        animationFrame$.onPreRender$.add(this.update);
    }
    checkOnlineStatus() {
        if (serverConnection.isServerOnline()) {
            this.clearMessage(0);
        }
        else {
            this.addMessage(0, "Connection to server lost");
        }
        if (!peerService.peerMgr) {
            return false;
        }
        let connectionStateValid = true;
        let signalingStateValid = true;
        let iceConnectionStateValid = true;
        let disconnectedTimeout = false;
        for (const peer of peerService.peerMgr.peers.values()) {
            const peerConnection = peer.webRTC.getPeerConnection();
            if (!peerConnection)
                continue;
            if (peer.webRTC.getWasDisconnectedTimeout()) {
                disconnectedTimeout = true;
            }
            const connectionState = peer.webRTC.getConnectionState();
            if (connectionState !== "connected") {
                connectionStateValid = false;
            }
            const signalingState = peerConnection.signalingState;
            if (signalingState !== "stable") {
                signalingStateValid = false;
            }
            const iceConnectionState = peerConnection.iceConnectionState;
            if (iceConnectionState !== "completed" && iceConnectionState !== "connected") {
                iceConnectionStateValid = false;
            }
        }
        if (this.peerConnectionError && connectionStateValid) {
            this.peerConnectionError = false;
            this.clearMessage(1);
        }
        if (this.peerSignalingError && signalingStateValid) {
            this.peerSignalingError = false;
            this.clearMessage(2);
        }
        if (this.peerIceConnectionError && iceConnectionStateValid) {
            this.peerIceConnectionError = false;
            this.clearMessage(3);
        }
        if (disconnectedTimeout) {
            this.addMessage(4, "Timeout while connecting to a peer");
        }
        else {
            this.clearMessage(4);
        }
        return true;
    }
    createMessage(msg) {
        if (!this.textMesh) {
            this.textMesh = new Text();
        }
        this.textMesh.textAlign = "center";
        this.textMesh.anchorX = "center";
        this.textMesh.anchorY = "middle";
        this.textMesh.maxWidth = MAX_MESSAGE_WIDTH;
        this.textMesh.text = msg;
        this.textMesh.gpuAccelerateSDF = true;
        this.textMesh.position.copy(new Vector3(0, 0.01, 0));
        this.textMesh.scale.copy(TEXT_SCALE);
        this.textMesh.quaternion.copy(TEXT_ORIENTATION);
        this.textMesh.rotateY(Math.PI);
        return this.textMesh;
    }
    addMessage(idx, msg) {
        var _a;
        // TODO: This is only a temporary solution, we should use the user list and conference view in the new ui to show if a peer is connected or not,
        // but for now, we just want to avoid the annoying modal error dialog:
        if (idx === 1 || idx === 3) {
            console.error(msg);
            return;
        }
        if (renderManager.isXRPresenting()) {
            const data = this.statusMessages[idx];
            if (data && !data.textMesh) {
                data.textMesh = this.createMessage(msg);
                (_a = data.textMesh) === null || _a === void 0 ? void 0 : _a.sync();
                this.needsTransformUpdate = true;
            }
        }
        else {
            MessageService.showMessage("error", msg);
        }
    }
    clearMessage(idx) {
        const data = this.statusMessages[idx];
        if (data && data.textMesh) {
            data.textMesh.geometry.dispose();
            if (!Array.isArray(data.textMesh.material)) {
                data.textMesh.material.dispose();
            }
            else {
                for (const material of data.textMesh.material) {
                    material.dispose();
                }
            }
            data.textMesh = undefined;
            this.needsTransformUpdate = true;
        }
    }
    getNamesFromPeersWithICEEerrors() {
        let names = "";
        let i = 0;
        for (const clientId of this.peersWithICEError.keys()) {
            // ignore unknown users
            if (!usersService.hasUserByClientId(clientId)) {
                this.peersWithICEError.delete(clientId);
                continue;
            }
            const name = usersService.getDisplayName(clientId);
            if (i === 0) {
                names += name;
            }
            else if (i <= 2) {
                names += "," + name;
            }
            else {
                names += ",...";
                break;
            }
            i++;
        }
        return names;
    }
}
